/**
 * While stage-2 is building with only the seed queued, nudge the server once
 * when the seed is within RADIO_LOW_QUEUE_SEC of ending.
 */

import { useMaestroStore } from '../store/maestroStore';
import { diagnosticLog } from '../utils/diagnosticLogger';
import { smartPlaylistService } from '../services/smartPlaylistService';
import {
  getRadioPlaylistId,
  getRadioSessionBuildStatus,
  isRadioExpectingMore,
  markLowQueueKickSent,
  RADIO_LOW_QUEUE_SEC,
} from './ellaRadioSessionState';
import { isEllaRadioBuildPollActive } from './ellaRadioBuildPoll';

let _unsub: (() => void) | null = null;
let _active = false;

function remainingSeconds(state: ReturnType<typeof useMaestroStore.getState>): number | null {
  const dur = Number(state.duration || 0);
  const pos = Number(state.position || 0);
  if (!Number.isFinite(dur) || dur <= 0) return null;
  return Math.max(0, dur - pos);
}

async function maybeKickBuild(remainingSec: number): Promise<void> {
  if (!markLowQueueKickSent()) return;
  const playlistId = getRadioPlaylistId();
  if (!playlistId) return;

  diagnosticLog('warn', 'js:radio', 'radio_low_queue_warning', {
    playlistId,
    remainingSec,
    queueLength: useMaestroStore.getState().queue.length,
    buildStatus: getRadioSessionBuildStatus(),
  });

  try {
    await smartPlaylistService.kickStationBuild(playlistId);
  } catch (e) {
    console.warn('[ellaRadioLowQueueWatch] kick-build failed:', (e as Error)?.message || e);
  }
}

function onStoreChange(state: ReturnType<typeof useMaestroStore.getState>): void {
  if (!_active || !isEllaRadioBuildPollActive()) return;
  if (!isRadioExpectingMore()) return;
  if (getRadioSessionBuildStatus() !== 'building') return;
  if (state.queue.length !== 1) return;

  const remaining = remainingSeconds(state);
  if (remaining == null || remaining > RADIO_LOW_QUEUE_SEC) return;

  void maybeKickBuild(remaining);
}

export function startEllaRadioLowQueueWatch(): void {
  stopEllaRadioLowQueueWatch();
  _active = true;
  _unsub = useMaestroStore.subscribe(onStoreChange);
}

export function stopEllaRadioLowQueueWatch(): void {
  _active = false;
  if (_unsub) {
    _unsub();
    _unsub = null;
  }
}
